import React, { useMemo, useState } from "react";

import RoundedTabs from "../../../../../../components/ui/tabs/rounded-tabs/RoundedTabs";
import RemoveFromLibrary from "../../../../../../components/ui/add-to-library-button/RemoveFromLibrary";
import DeleteTemplateModal from "../../DeleteTemplateModal";
import { ButtonWidget as Button } from "@caelor/ui";
import {
  ButtonTemplateContainer,
  ButtonTemplatesContainer,
  OverviewButtonTemplateContainer,
} from "./styles";
import { getButtonTemplates } from "./button-templates";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import { Column } from "../../styles";
import { buttonLibraryService } from "@Shared/Service/ButtonLibraryService";
import { buttonStores } from "../../../../../../stores/export-stores";
import { useButtonStore } from "../../../../../../stores/use-button-store";
import { MACRO_KEY } from "../../../../../defaults";
import { useMigrationStore } from "../../../../../../stores/use-migration-store";
import { buildButtonData } from "../../../../editor/handlers/macros/button-inline-migration";
import { useTranslation } from "react-i18next";

const getButtonData = (template, buttonData) => ({
  ...template,
  backgroundColor: template.backgroundColor,
  accentColor: template.accentColor,
  labelColor: template.labelColor,
  newTab: buttonData.newTab || template.newTab,
  border: template.border,
  label: buttonData.label || template.label,
  icon: buttonData.icon || template.icon,
  url: buttonData.url || template.url,
  styleVariant: template.styleVariant,
});

export const OverviewTemplates = () => {
  const { t } = useTranslation();
  const { data: buttonData, setData } = useButtonStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const { setDrawers, styleguideSetup, setMacro, colorPalette } =
    useOpticsStore((state) => ({
      setDrawers: state.setDrawers,
      styleguideSetup: state.styleguideSetup,
      setMacro: state.setMacro,
      colorPalette: state.colorPalette,
    }));

  const templates = useMemo(
    () =>
      getButtonTemplates({
        buttonCornerRadius: styleguideSetup.buttonCornerRadius,
        buttonColor: styleguideSetup.buttonColor,
        selectedColors: colorPalette,
        isInOverview: true,
        t,
      }),
    [styleguideSetup, colorPalette]
  );

  return (
    <ButtonTemplatesContainer
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))" }}
    >
      {templates.map((data) => (
        <OverviewButtonTemplateContainer
          key={data.id}
          isSelected={data.id === buttonData.id}
          onClick={() => {
            setMacro({ key: MACRO_KEY.BUTTON });
            setData(getButtonData(data, buttonData));
            setDrawers({ templates: false, settings: true });
          }}
        >
          <Button data={data} />
        </OverviewButtonTemplateContainer>
      ))}
    </ButtonTemplatesContainer>
  );
};

const FromLibrary = ({ buttons, editorMacro }) => {
  const [buttonToDelete, setButtonToDelete] = useState();

  const { data: buttonData, setData } = buttonStores[editorMacro]((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const { isMigrationActive, oldMacroData } = useMigrationStore();

  const {
    setDrawers,
    hasStyleguidePermission,
    setLibraryButtons,
    isDrawerModalOpen,
    setIsDrawerModalOpen,
  } = useOpticsStore((state) => ({
    setDrawers: state.setDrawers,
    hasStyleguidePermission: state.hasStyleguidePermission,
    setLibraryButtons: state.setLibraryButtons,
    isDrawerModalOpen: state.isDrawerModalOpen,
    setIsDrawerModalOpen: state.setIsDrawerModalOpen,
  }));

  const removeButtonFromLibrary = async (id) => {
    setLibraryButtons([...buttons.filter((btn) => btn.id !== id)]);
    try {
      await buttonLibraryService.deleteButton(id);
    } catch (error) {
      console.log("Failed to remove button from library");
    }
  };

  return (
    <>
      <ButtonTemplatesContainer>
        {buttons.map((data) => (
          <ButtonTemplateContainer
            key={data.id}
            isSelected={data.id === buttonData.id}
            onClick={() => {
              if (
                isMigrationActive &&
                oldMacroData.macro === MACRO_KEY.BUTTON_INLINE
              ) {
                const builtData = buildButtonData(data, oldMacroData.data);
                setData(getButtonData(builtData, buttonData));
              } else {
                setData(getButtonData({ ...data }, buttonData));
              }

              setDrawers({ templates: false, settings: true });
            }}
          >
            <Button data={data} editorMacro={editorMacro} />
            {hasStyleguidePermission && (
              <RemoveFromLibrary
                onClick={(e) => {
                  e.stopPropagation();

                  setIsDrawerModalOpen(true);
                  setButtonToDelete(data.id);
                }}
              />
            )}
          </ButtonTemplateContainer>
        ))}
      </ButtonTemplatesContainer>

      <DeleteTemplateModal
        isOpen={isDrawerModalOpen}
        onClose={() => setIsDrawerModalOpen(false)}
        onConfirm={() => removeButtonFromLibrary(buttonToDelete)}
        macroName="button"
      />
    </>
  );
};

const BuiltTemplates = ({ editorMacro }) => {
  const { t } = useTranslation();

  const { data: buttonData, setData } = buttonStores[editorMacro]((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const { setDrawers, styleguideSetup, colorPalette } = useOpticsStore(
    (state) => ({
      setDrawers: state.setDrawers,
      styleguideSetup: state.styleguideSetup,
      colorPalette: state.colorPalette,
    })
  );

  const { isMigrationActive, oldMacroData } = useMigrationStore();

  const { buttonColor, buttonCornerRadius } = styleguideSetup;

  const color = useMemo(() => {
    return {
      light: {
        accent: buttonColor?.light?.accent || oldMacroData?.data?.buttonColor,
        text: buttonColor?.light?.text || oldMacroData?.data?.buttonFontColor,
      },
      dark: {
        accent: buttonColor?.light?.accent || oldMacroData?.data?.buttonColor,
        text: buttonColor?.light?.text || oldMacroData?.data?.buttonFontColor,
      },
    };
  }, [buttonColor, oldMacroData]);

  const radius = useMemo(() => {
    return buttonCornerRadius || oldMacroData?.data?.buttonRadius;
  }, [buttonCornerRadius, oldMacroData]);

  const templates = useMemo(
    () =>
      getButtonTemplates({ buttonColor: color, buttonCornerRadius: radius, t }),
    [color, radius, t]
  );

  const applyOverrides = (data, colorPalette) => {
    if (!colorPalette) return data;

    return {
      ...data,
      backgroundColor: {
        ...(data.backgroundColor || {}),
        light: colorPalette?.accent?.light || data.backgroundColor?.light,
      },
      border: {
        ...(data.border || {}),
        light: colorPalette?.accent?.light || data.border?.light,
      },
      labelColor: {
        ...(data.labelColor || {}),
        light: colorPalette?.text?.light || data.labelColor?.light,
      },
    };
  };

  return (
    <ButtonTemplatesContainer>
      {templates.map((data) => {
        const buttonDataWithOverride = applyOverrides(data, colorPalette);
        return (
          <ButtonTemplateContainer
            key={data.id}
            isSelected={data.id === buttonData.id}
            onClick={() => {
              if (
                isMigrationActive &&
                oldMacroData.macro === MACRO_KEY.BUTTON_INLINE
              ) {
                const builtData = buildButtonData(
                  buttonDataWithOverride,
                  oldMacroData.data
                );
                setData(getButtonData(builtData, buttonData));
              } else {
                setData(getButtonData(buttonDataWithOverride, buttonData));
              }
              setDrawers({ templates: false, settings: true });
            }}
          >
            <Button data={buttonDataWithOverride} />
          </ButtonTemplateContainer>
        );
      })}
    </ButtonTemplatesContainer>
  );
};

const Tabs = [
  {
    id: "fl",
    i18n: "from-library",
    label: "From library",
    Content: FromLibrary,
  },
  {
    id: "all",
    i18n: "all-templates",
    label: "All templates",
    Content: BuiltTemplates,
  },
];

const ButtonTemplates = ({ editorMacro }) => {
  const { libraryButtons, selectedTemplateTab, setSelectedTemplateTab } =
    useOpticsStore((state) => ({
      libraryButtons: state.libraryButtons,
      selectedTemplateTab: state.selectedTemplateTab,
      setSelectedTemplateTab: state.setSelectedTemplateTab,
    }));

  const selectedTab = selectedTemplateTab.button;

  const { Content } = Tabs.find(({ id }) => id === selectedTab);

  return (
    <Column>
      <RoundedTabs
        tabs={Tabs.filter(({ id }) => libraryButtons?.length || id !== "fl")}
        selectedTab={selectedTab}
        setSelectedTab={(value) =>
          setSelectedTemplateTab({ macro: "button", tab: value })
        }
      />

      <Content
        buttons={libraryButtons}
        selectedTab={selectedTab}
        editorMacro={editorMacro}
      />
    </Column>
  );
};

export default ButtonTemplates;
